import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MapPin,
  Mail,
  Phone,
  MessagesSquare,
  Clock,
  Send,
  ChevronRight,
  HelpCircle,
  Navigation,
} from 'lucide-react';
import {
  DEALERSHIP_ADDRESS,
  DEALERSHIP_EMAIL,
  DEALERSHIP_PHONE_DISPLAY,
} from '../../constants/dealershipContact';
import DealershipPhoneLink from '../../components/ALLJsx/DealershipPhoneLink';
import axios from 'axios';
import { API_URL } from '../../services/api';
import { useLocale } from '../../contexts/LocaleContext';
import { openMyCarSlotChat } from '../../utils/openChat';
import Navbar from '../../components/ALLJsx/Navbar';
import Footer from '../../components/ALLJsx/Footer';
import '../AllCss/Contact.css';

const HOURS = [
  { key: 'weekday', label: 'Mon–Fri', time: '9:00 AM – 7:00 PM' },
  { key: 'saturday', label: 'Sat', time: '9:00 AM – 6:00 PM' },
  { key: 'sunday', label: 'Sun', time: '10:00 AM – 5:00 PM' },
];

function Contact() {
  const { t } = useLocale();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/api/contact`, formData);
      if (response.data.success) {
        setSubmitted(true);
        setTimeout(() => {
          setSubmitted(false);
          setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        }, 5000);
      }
    } catch (err) {
      console.error('Error submitting contact form:', err);
      setError(err.response?.data?.message || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page">
      <Navbar />

      <main>
        <section className="contact-hero" aria-labelledby="contact-page-title">
          <div className="contact-shell contact-hero-inner">
            <p className="contact-eyebrow">{t('contact.pageEyebrow')}</p>
            <h1 id="contact-page-title" className="contact-title">
              {t('contact.getInTouch')}
            </h1>
            <p className="contact-lead">{t('contact.getInTouchSub')}</p>
          </div>
        </section>

        <section className="contact-actions" aria-label={t('contact.quickActionsTitle')}>
          <div className="contact-shell">
            <h2 className="contact-section-label">{t('contact.quickActionsTitle')}</h2>
            <div className="contact-action-grid">
              <button type="button" className="contact-action-card" onClick={openMyCarSlotChat}>
                <span className="contact-action-icon" aria-hidden>
                  <MessagesSquare size={22} strokeWidth={2} />
                </span>
                <span className="contact-action-body">
                  <span className="contact-action-title">{t('contact.chatAction')}</span>
                  <span className="contact-action-desc">{t('contact.chatActionDesc')}</span>
                </span>
                <ChevronRight className="contact-action-arrow" size={18} aria-hidden />
              </button>

              <DealershipPhoneLink className="contact-action-card">
                <span className="contact-action-icon" aria-hidden>
                  <Phone size={22} strokeWidth={2} />
                </span>
                <span className="contact-action-body">
                  <span className="contact-action-title">{t('contact.phoneAction')}</span>
                  <span className="contact-action-desc">{DEALERSHIP_PHONE_DISPLAY}</span>
                </span>
                <ChevronRight className="contact-action-arrow" size={18} aria-hidden />
              </DealershipPhoneLink>

              <a href={`mailto:${DEALERSHIP_EMAIL}`} className="contact-action-card">
                <span className="contact-action-icon" aria-hidden>
                  <Mail size={22} strokeWidth={2} />
                </span>
                <span className="contact-action-body">
                  <span className="contact-action-title">{t('contact.emailAction')}</span>
                  <span className="contact-action-desc">{DEALERSHIP_EMAIL}</span>
                </span>
                <ChevronRight className="contact-action-arrow" size={18} aria-hidden />
              </a>

              <Link to="/faq-support" className="contact-action-card">
                <span className="contact-action-icon" aria-hidden>
                  <HelpCircle size={22} strokeWidth={2} />
                </span>
                <span className="contact-action-body">
                  <span className="contact-action-title">{t('contact.faqAction')}</span>
                  <span className="contact-action-desc">{t('contact.faqActionDesc')}</span>
                </span>
                <ChevronRight className="contact-action-arrow" size={18} aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        <section className="contact-main">
          <div className="contact-shell contact-main-grid">
            <aside className="contact-aside" aria-labelledby="contact-aside-title">
              <h2 id="contact-aside-title" className="contact-aside-title">
                {t('contact.visitDealership')}
              </h2>
              <p className="contact-aside-lead">{t('contact.contactInfoText')}</p>

              <div className="contact-info-block">
                <MapPin className="contact-info-icon" size={20} strokeWidth={2} aria-hidden />
                <div>
                  <p className="contact-info-label">{t('contact.address')}</p>
                  <p className="contact-info-value">{DEALERSHIP_ADDRESS.line1}</p>
                  <p className="contact-info-value">{DEALERSHIP_ADDRESS.line2}</p>
                  <a
                    href={DEALERSHIP_ADDRESS.mapsDirections}
                    className="contact-info-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Navigation size={14} aria-hidden />
                    {t('contact.directions')}
                  </a>
                </div>
              </div>

              <div className="contact-info-block">
                <Clock className="contact-info-icon" size={20} strokeWidth={2} aria-hidden />
                <div>
                  <p className="contact-info-label">{t('contact.businessHours')}</p>
                  <ul className="contact-hours-list">
                    {HOURS.map((row) => (
                      <li key={row.key}>
                        <span>{row.label}</span>
                        <span>{row.time}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="contact-info-block">
                <Phone className="contact-info-icon" size={20} strokeWidth={2} aria-hidden />
                <div>
                  <p className="contact-info-label">{t('contact.phone')}</p>
                  <DealershipPhoneLink className="contact-info-value contact-info-value--link">
                    {DEALERSHIP_PHONE_DISPLAY}
                  </DealershipPhoneLink>
                </div>
              </div>

              <div className="contact-info-block">
                <Mail className="contact-info-icon" size={20} strokeWidth={2} aria-hidden />
                <div>
                  <p className="contact-info-label">{t('contact.email')}</p>
                  <a href={`mailto:${DEALERSHIP_EMAIL}`} className="contact-info-value contact-info-value--link">
                    {DEALERSHIP_EMAIL}
                  </a>
                </div>
              </div>

              <div className="contact-map-preview">
                <iframe
                  title={DEALERSHIP_ADDRESS.full}
                  src={DEALERSHIP_ADDRESS.mapsEmbed}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </aside>

            <div className="contact-form-panel">
              <div className="contact-form-head">
                <h2 className="contact-form-title">{t('contact.sendMessage')}</h2>
                <p className="contact-form-lead">{t('contact.formLead')}</p>
              </div>

              {submitted && (
                <div className="contact-alert contact-alert--success" role="status">
                  {t('contact.successMessage')}
                </div>
              )}

              {error && (
                <div className="contact-alert contact-alert--error" role="alert">
                  {error}
                </div>
              )}

              <form className="contact-form" onSubmit={handleSubmit} noValidate>
                <div className="contact-field">
                  <label htmlFor="contact-name">{t('contact.fullName')}</label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    autoComplete="name"
                    placeholder="John Doe"
                  />
                </div>

                <div className="contact-field-row">
                  <div className="contact-field">
                    <label htmlFor="contact-email">{t('contact.email')}</label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      autoComplete="email"
                      placeholder="john@example.com"
                    />
                  </div>
                  <div className="contact-field">
                    <label htmlFor="contact-phone">{t('contact.phone')}</label>
                    <input
                      type="tel"
                      id="contact-phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      autoComplete="tel"
                      placeholder="(555) 555-5555"
                    />
                  </div>
                </div>

                <div className="contact-field">
                  <label htmlFor="contact-subject">{t('contact.subject')}</label>
                  <select
                    id="contact-subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  >
                    <option value="">{t('contact.selectSubject')}</option>
                    <option value="general">{t('contact.generalInquiry')}</option>
                    <option value="sales">{t('contact.salesQuestion')}</option>
                    <option value="financing">{t('home.financingTitle')}</option>
                    <option value="service">{t('contact.serviceSupport')}</option>
                    <option value="trade">{t('contact.tradeIn')}</option>
                    <option value="other">{t('contact.other')}</option>
                  </select>
                </div>

                <div className="contact-field">
                  <label htmlFor="contact-message">{t('contact.message')}</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder={t('contact.messagePlaceholder')}
                  />
                </div>

                <button type="submit" className="contact-submit" disabled={loading}>
                  <Send size={18} strokeWidth={2.25} aria-hidden />
                  {loading ? t('contact.sending') : t('contact.sendButton')}
                </button>
              </form>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default Contact;
